// Content for the two enterprise application pathways on /solutions/heart.
// No pricing anywhere — pricing is quoted per health system after scoping.

export const SOFTWARE_APPS = {
  "rebate-tracking": {
    slug: "rebate-tracking",
    code: "H.E.A.R.T",
    name: "H.E.A.R.T",
    expansion: "Healthcare Earned Advanced Rebate Tracking",
    tagline: "Every rebate dollar you earned, tracked to the contract line.",
    box: "/api/static/software/heart-box-3d.jpg",
    boxBack: "/api/static/software/heart-box-back.jpg",
    boxBackAlt: "Back of the H.E.A.R.T software box showing system requirements",
    boxAlt: "H.E.A.R.T Healthcare Earned Advanced Rebate Tracking enterprise software package by Techinquest",
    accent: "#0EA5E9",
    badge: "Rebate management",
    edition: "Enterprise Edition",
    summary:
      "Rebate agreements live in spreadsheets, tiers are checked after the period closes, and the " +
      "near-misses are found too late to act on. H.E.A.R.T recalculates your position on every " +
      "purchase and turns a shortfall into assigned work while the period is still open.",
    topFeatures: [
      { t: "Live tier monitoring", d: "Position recalculated on every purchase, so near-misses surface while the period is still open." },
      { t: "Five alert types", d: "Near-miss, shortfall, variance, expiry and overdue — each with dollars, days and a named owner." },
      { t: "Rebate & contract tracking", d: "Every agreement, tier table and deadline in one governed system rather than a spreadsheet." },
      { t: "Tasks & audit trail", d: "Alerts become assigned work with escalation paths and a complete, exportable history." },
      { t: "Analytics & dashboards", d: "Board-ready views that drill from the region down to the individual contract line." },
      { t: "Supplier dispute portal", d: "Disputes resolved where the supplier can answer, with dollars assigned to the right department." },
    ],
    competencies: [
      "Tier and threshold modelling across multi-year agreements",
      "Variance detection between earned, accrued and received",
      "Period-close reconciliation with a defensible audit trail",
      "Escalation ownership mapped to your finance structure",
      "Executive reporting from system down to contract line",
    ],
    whoFor: [
      "Chief Financial Officer",
      "VP / Director of Supply Chain",
      "Contract management",
      "Internal audit",
    ],
    // The three claims actually printed on the back of the box, with the
    // sentence an executive gets when they read past the heading.
    printedOnBack: [
      { h: "Live tier monitoring", d: "Your position against every tier is recalculated on each purchase, so a near-miss surfaces while the quarter is still open and can still be acted on." },
      { h: "Five alert types", d: "Near-miss, shortfall, variance, expiry and overdue. Each one carries the dollars at stake, the days remaining and the name of the person who owns it." },
      { h: "Full audit trail", d: "Every calculation, alert, task and supplier response is retained and exportable, so the rebate file holds up in front of internal audit." },
    ],
    story: [
      {
        img: "/api/static/software/story/heart-exec.jpg",
        alt: "Hospital executive leadership reviewing performance charts in a boardroom",
        k: "The quarterly review",
        c: "Leadership asks what was earned. The answer arrives from a spreadsheet nobody can defend line by line.",
      },
      {
        img: "/api/static/software/story/heart-finance.jpg",
        alt: "Healthcare finance leader reviewing contract documents at her desk",
        k: "The reconciliation",
        c: "Earned, accrued and received rarely agree. Finding the gap after the period closes means finding it too late.",
      },
      {
        img: "/api/static/software/story/heart-floor.jpg",
        alt: "Hospital supply chain technician checking stock on a tablet in a clean storeroom",
        k: "Where the tier moves",
        c: "Every requisition on the floor shifts the position. H.E.A.R.T recalculates it the moment the purchase lands.",
      },
      {
        img: "/api/static/software/story/heart-dock.jpg",
        alt: "Materials management clerk checking a delivery on a hospital receiving dock",
        k: "Where the spend is real",
        c: "A rebate is only ever as good as the receiving record behind it. The dock is the first link in the audit trail.",
      },
    ],
    kitFirst: {
      t: "Your agreement library, loaded",
      d: "We load every rebate agreement, tier table and deadline you hand over, then reconcile your last closed period inside the system so you can see the current position on day one.",
    },
  },
  "implant-management": {
    slug: "implant-management",
    code: "IMS",
    name: "IMS",
    expansion: "Implant Management System",
    tagline: "Bill-only implant procedures, priced to contract before the invoice lands.",
    box: "/api/static/software/ims-box-3d.jpg",
    boxBack: "/api/static/software/ims-box-back.jpg",
    boxBackAlt: "Back of the IMS software box showing system requirements",
    boxAlt: "IMS Implant Management System enterprise software package by Techinquest",
    accent: "#14B8A6",
    badge: "Bill-only implants",
    edition: "Enterprise Edition",
    summary:
      "Bill-only implants arrive as paperwork after the case is closed, priced off whatever the " +
      "rep wrote down. IMS puts the case, the contract price and the supplier submission in one " +
      "governed workflow, so the charge is checked against contract before it reaches accounts payable.",
    topFeatures: [
      { t: "Bill-only case capture", d: "Each implant procedure recorded with the items actually used, against the case and the surgeon." },
      { t: "Contract price enforcement", d: "Lines are checked against the contracted price before the charge is accepted." },
      { t: "Supplier submission portal", d: "Suppliers submit their documentation where it can be matched, not by email attachment." },
      { t: "Approval workflow", d: "Product approval ahead of surgery, with the request tracked to a decision and an owner." },
      { t: "Vendor compliance status", d: "Physician and vendor credential or vaccination status visible before the rep is on site." },
      { t: "Full chargeable audit trail", d: "Every line, approval and price exception retained and exportable for audit." },
    ],
    competencies: [
      "Case-level implant capture tied to the surgical schedule",
      "Contract price validation before accounts payable sees the charge",
      "Supplier-side submission with matching and exception handling",
      "Pre-surgical product approval with named accountability",
      "Credential and compliance gating for vendor site access",
    ],
    whoFor: [
      "Chief Operating Officer",
      "Perioperative / OR leadership",
      "Value analysis committee",
      "Accounts payable",
    ],
    printedOnBack: [
      { h: "Bill-only case capture", d: "The implants actually used are recorded against the case and the surgeon at the point of use, not reconstructed from paperwork days later." },
      { h: "Contract price check", d: "Every line is tested against the contracted price before the charge is accepted, so an off-contract figure is stopped before accounts payable ever sees it." },
      { h: "Supplier portal", d: "Reps submit their documentation where it can be matched to the case, with exceptions routed to an owner instead of sitting in somebody's inbox." },
    ],
    story: [
      {
        img: "/api/static/software/story/ims-or.jpg",
        alt: "Surgical team working together in a bright modern operating theatre",
        k: "In the room",
        c: "The implant goes in. What it costs is decided by whatever the rep writes down, and nobody in the room is checking a contract.",
      },
      {
        img: "/api/static/software/story/ims-receiving.jpg",
        alt: "Perioperative staff checking sealed sterile implant trays on arrival",
        k: "At receiving",
        c: "Bill-only stock arrives with the case, not against a purchase order. Without capture at the door there is nothing to match the invoice to.",
      },
      {
        img: "/api/static/software/story/ims-supply.jpg",
        alt: "Technician reviewing wrapped instrument trays in a sterile storage room",
        k: "In the supply room",
        c: "Consignment trays, loaner sets and what the rep left behind. IMS keeps the record of what was used and what was returned.",
      },
      {
        img: "/api/static/software/story/ims-committee.jpg",
        alt: "Clinicians and administrators reviewing products at a value analysis committee",
        k: "At value analysis",
        c: "The committee approves the product. IMS is where that approval is recorded, enforced at the price and proven afterwards.",
      },
    ],
    kitFirst: {
      t: "Your contract price file, loaded",
      d: "We load the contracted implant pricing, the approval matrix and the vendor credential list, then run a closed month of real cases through the system so you can see the exceptions it would have caught.",
    },
  },
};

// Shared platform facts — identical for both applications.
export const PLATFORM = {
  builtBy: {
    name: "Techinquest",
    logo: "/api/static/software/techinquest-logo.png",
    role: "Software developer",
    line: "Built by Techinquest. Distributed and implemented by Sterile Mate.",
  },
  enterprise: [
    { t: "Enterprise deployment", d: "Multi-facility, multi-entity rollouts across a whole health system, not a single department tool." },
    { t: "Enterprise security", d: "SSO with Entra ID, MFA, role-based access and immutable audit logs on every record." },
    { t: "Enterprise integration", d: "Reads the system of record you already run — Workday, Oracle, Infor, SAP or Dynamics 365." },
    { t: "Enterprise support", d: "Implementation, configuration and training delivered with Techinquest's engineering team." },
  ],
  compatibility: [
    { t: "Runs in the browser", d: "Cloud SaaS on AWS. Nothing to install, no servers to rack, no desktop client." },
    { t: "Every device, same experience", d: "Desktop, laptop, iPad and iPhone — the interface follows the user." },
    { t: "Reads your ERP, not your EHR", d: "Fed from Workday, Oracle, Infor, SAP or Dynamics 365, where purchases, invoices and remittances live. No Epic integration required." },
    { t: "SSO with Entra ID and MFA", d: "Role-based access that mirrors how your finance team is already structured." },
    { t: "Encrypted in transit and at rest", d: "Secured data stores with immutable audit logs." },
    { t: "Runs alongside your GPO", d: "No membership change, no contract migration, no rip and replace." },
  ],
  erps: ["Workday", "Oracle", "Infor", "SAP", "Dynamics 365"],
};

// What the implementation team physically hands over at kickoff.
export const IMPLEMENTATION_KIT = {
  intro:
    "Nothing ships in a box, but the kickoff is still a handover. This is what your team " +
    "receives in the first session, and what we are accountable for delivering before go-live.",
  items: [
    { n: "01", t: "Scoping and configuration workbook", d: "The facilities, entities, modules and approval structure we are configuring, written down and signed off before anything is built." },
    { n: "02", t: "Integration specification", d: "The exact fields we read from your ERP, the extract schedule and who owns it on each side. Reviewed with your IT team, not around them." },
    { n: "03", t: "Named implementation lead", d: "One engineer from Techinquest who runs your build, attends your calls and stays through go-live. Not a ticket queue." },
    { n: "04", t: "Role and access matrix", d: "Every role mapped to your Entra ID groups, so access mirrors how your organisation is already structured on day one." },
    { n: "05", t: "Configured test environment", d: "Your own environment loaded with your data, so the team validates against real records before anything goes live." },
    { n: "06", t: "Role-based training", d: "Separate sessions for administrators, day-to-day users and your supplier-facing staff, delivered live and recorded for the people who join later." },
    { n: "07", t: "Go-live runbook and hypercare", d: "A dated cutover plan with owners, followed by a defined hypercare window with direct access to the engineering team." },
    { n: "08", t: "Quarterly review cadence", d: "A standing review of what the system found, what it is worth and what to tune next. The handover does not end at go-live." },
  ],
};

export const APP_ORDER = ["rebate-tracking", "implant-management"];

// Printed on the back of the box, like real retail software.
export const SYSTEM_REQUIREMENTS = [
  { k: "Deployment", v: "Cloud SaaS hosted on AWS. No on-premise server, no desktop install." },
  { k: "Browser", v: "Current Chrome, Edge, Safari or Firefox. No plug-ins required." },
  { k: "Devices", v: "Desktop, laptop, iPad and iPhone. Responsive to the device in hand." },
  { k: "Identity", v: "SSO via Microsoft Entra ID with MFA. Role-based access control." },
  { k: "Data source", v: "Workday, Oracle, Infor, SAP or Dynamics 365. No EHR integration required." },
  { k: "Network", v: "Standard HTTPS over port 443. No inbound firewall changes." },
  { k: "Security", v: "Encrypted in transit and at rest, with immutable audit logging." },
  { k: "Implementation", v: "Configured and trained with the Techinquest engineering team." },
];

// Side-by-side matrix for executives weighing both systems.
// true = included, false = not applicable to that system.
export const COMPARISON = [
  { group: "What it governs", rows: [
    { f: "Rebate and contract agreements", a: true, b: false },
    { f: "Tier and threshold tracking", a: true, b: false },
    { f: "Bill-only implant cases", a: false, b: true },
    { f: "Contract price enforcement on charges", a: false, b: true },
    { f: "Pre-surgical product approval", a: false, b: true },
  ] },
  { group: "Money", rows: [
    { f: "Recovers rebate dollars already earned", a: true, b: false },
    { f: "Prevents overpayment before AP posts it", a: false, b: true },
    { f: "Variance between earned, accrued and received", a: true, b: false },
    { f: "Price exception reporting", a: false, b: true },
  ] },
  { group: "Operating model", rows: [
    { f: "Alerts with dollars, days and a named owner", a: true, b: true },
    { f: "Tasks, escalation and assigned accountability", a: true, b: true },
    { f: "Supplier-facing portal", a: true, b: true },
    { f: "Vendor credential and compliance gating", a: false, b: true },
    { f: "Executive dashboards and analytics", a: true, b: true },
  ] },
  { group: "Enterprise platform", rows: [
    { f: "Multi-facility, multi-entity deployment", a: true, b: true },
    { f: "SSO with Entra ID and MFA", a: true, b: true },
    { f: "Immutable audit trail, exportable", a: true, b: true },
    { f: "Reads your ERP, not your EHR", a: true, b: true },
    { f: "Runs alongside your existing GPO", a: true, b: true },
  ] },
];
